// --- Window config / BE Input ---

import illustrations from '../components/Illustration/components';

export type LayoutT = 'collapsed' | 'narrow' | 'wide' | 'fullscreen';

export enum Position {
  TopRight = 'TopRight',
  TopLeft = 'TopLeft',
  BottomRight = 'BottomRight',
  BottomLeft = 'BottomLeft',
  CenterLeft = 'CenterLeft',
  CenterRight = 'CenterRight',
}

export enum Appearance {
  Small = 'Small',
  Large = 'Large',
}

export type Positions = {
  desktop: Position;
  mobile: Position;
};

export type Appearances = {
  desktop: Appearance;
  mobile: Appearance;
};

export type StyleParams = {
  /* Should be valid CSS value e.g. "5px 10px" or "1rem 1rem 0rem 1rem" */
  bodyMargin?: string;
  /* Should be valid CSS value e.g. "5px 10px" or "1rem 1rem 0rem 1rem" */
  bodyPadding?: string;
};

export type WindowIframeApp = {
  type: 'iframe';
  appUrl: string;
  appParams: object;
  slug: string;
  title: ContentString;
  description: ContentString;
  illustration: keyof typeof illustrations;
  imageUrl?: string;
  highlighted?: boolean;
  supportedLang?: Array<Locale>;
  styleParams?: {
    mobile?: StyleParams;
    desktop?: StyleParams;
  };
};

export type WindowApp = WindowIframeApp;

export type WindowConfig = {
  isDefaultConfig?: boolean;
  accountId: string;
  positions: Positions;
  appearances: Appearances;
  logo: {
    src: string;
  };
  apps: Array<WindowApp>;
  style: StyleConfig;
  zIndex: number;
};

export type Locale = 'nl' | 'en';

export type ContentString = Record<Locale, string>;

// --- In App types ---

export type IframeApp = {
  /**
   * The type of the app, dictates what should be used to render the app
   */
  type: 'iframe';

  /**
   * The iframe URL to be loaded
   */
  appUrl: string;

  /**
   * Any additional query parameter that should be set on the appUrl
   * will get query stringified and appended to appURL
   */
  appParams: object;

  /**
   * Slug used for internal paths e.g. /apps/[slug] and lookups of app configurations
   */
  slug: string;

  /**
   * The title that will shown in all places where the app is visible`
   */
  title: ContentString;

  /**
   * The description that will shown in all places where the app is visible`
   */
  description: ContentString;

  /*
   * Illustration used everywhere where the app is listed but not in the highlight
   */
  illustration: keyof typeof illustrations;

  /*
   * The image that should be used when the app is highlighted
   */
  imageUrl?: string;

  /*
   * Should this app be highlighted, there should only be 1 highlighted app at the same time
   * when we find more than 1 highlighted app, we will use the first ([0])
   */
  highlighted?: boolean;
  supportedLang?: Array<Locale>;
  styleParams?: {
    mobile?: StyleParams;
    desktop?: StyleParams;
  };
};

export type DHApp = IframeApp; // Expand this list when we add more types of apps

export type StyleConfigColorPair = {
  background: string;
  color: string;
};

export type StyleConfig = {
  success: StyleConfigColorPair;
  error: StyleConfigColorPair;
  primary: StyleConfigColorPair;
  secondary: StyleConfigColorPair;
};

// --- Messages ---

export type TriggerType =
  | 'deepLink'
  | 'command'
  | 'headerClick'
  | 'footerClick';

export type OpenMessage = {
  type: 'open';
  payload: {
    path?: string;
    queryParams?: string;
    triggerType?: TriggerType;
  };
};

export type CloseMessage = {
  type: 'close';
  payload: {};
};

export type DismissMessage = {
  type: 'dismiss';
  payload: {};
};

export type ClearDismissMessage = {
  type: 'clearDismiss';
  payload: {};
};

export type ResizeMessage = {
  type: 'resize';
  payload: {
    /**
     * CSS width including the unit
     */
    width: string;
    /**
     * CSS height including the unit
     */
    height: string;

    fullscreen?: boolean;

    /**
     * The position of the widget
     */
    position: Position;

    layout: LayoutT;
  };
};

export type ConfigLoaded = {
  type: 'configLoaded';
  payload: WindowConfig;
};

export type WidgetReadyMessage = {
  type: 'widgetReady';
  payload: {};
};

export type OverwriteConfigMessage = {
  type: 'overwriteConfig';
  payload: Partial<WindowConfig>;
};

/**
 * Should only ever be called from the error document
 */
export type ErrorDocumentMessage = {
  type: 'errorDocument';
  payload: {};
};

export type PostMessage =
  | ClearDismissMessage
  | CloseMessage
  | ConfigLoaded
  | DismissMessage
  | ErrorDocumentMessage
  | OpenMessage
  | OverwriteConfigMessage
  | ResizeMessage
  | WidgetReadyMessage;

export type OnMessageHandler = (message: PostMessage) => void;
export type SendMessage = (message: PostMessage) => void;

export type TrafficSource = {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
};

export type VirtualPageViewEvent = {
  event: 'VirtualPageView';
  path: string;
};

export type ExpandWidgetEvent = {
  event: 'ExpandWidget';
  triggerType?: TriggerType;
  path: string;
};

export type CollapseWidgetEvent = {
  event: 'CollapseWidget';
};

export type DismissWidgetEvent = {
  event: 'DismissWidget';
};

export type TrackingEvent =
  | CollapseWidgetEvent
  | DismissWidgetEvent
  | ExpandWidgetEvent
  | VirtualPageViewEvent;
